/**
 * „Lager“ -- der Baum mit allem, was darin ist (ADR 0013 §13, ADR 0016).
 *
 * **Feste Orte** stehen, wie sie in den Stammdaten angelegt sind,
 * **Packstücke** dort, wo sie gebucht sind. Die Knoten sind zugeklappt; wer
 * einen aufklappt, sieht darin die Plätze und Kisten, die Artikel --
 * gleiche zusammengefasst -- und was fehlt, mit dem Ort, an dem es gerade
 * ist. An jedem Knoten stehen Zähler und Vollständigkeit, und „Alles da“
 * bucht nach der Übersicht alles an seinen Stammplatz zurück (etwa am LKW
 * nach dem Einsatz).
 *
 * Alles aus der lokalen Kopie, also auch offline. Nur „+ Neues Packstück“
 * braucht Netz.
 */

import { useMemo, useState } from "react";
import { Link, useNavigate } from "react-router";
import { sitzungLesen } from "../api/klient.js";
import { useOnline } from "../api/online.js";
import { behaelterAnsicht } from "../lokal/behaelter.js";
import { useBestand, type Bestand } from "../lokal/bestand.js";
import { lagerBaum, lagerSuchen, type LagerKnoten, type LagerTreffer } from "../lokal/lager.js";
import { allesDaFuer, vollstaendigkeitVon } from "../lokal/stammplatz.js";
import { rueckmeldungGeben } from "../scan/Scanner.jsx";
import { AllesDa } from "../ui/AllesDa.jsx";
import { useAdressMenge, useAdressText } from "../ui/navigation.jsx";
import { Knopf, Ladeanzeige, Leer } from "../ui/bausteine.jsx";
import { useBuchungsmeldung } from "../ui/Buchungsmeldung.jsx";
import { FehltListe, FehltSchild, GehoertNach, GruppenZeile, kurz } from "../ui/Inhalt.jsx";
import { NeuesPackstueck } from "../ui/NeuesPackstueck.jsx";

/** Einrückung je Ebene. Schmal: Auch fünf Ebenen passen noch aufs Handy. */
const EINZUG_PX = 12;

interface KnotenEigenschaften {
  knoten: LagerKnoten;
  tiefe: number;
  /** Der Knoten darüber -- für „gehört nach …“ an einer Kiste, die woanders steht. */
  oben: string | null;
  bestand: Bestand;
  offen: ReadonlySet<string>;
  umschalten: (id: string) => void;
  treffer: LagerTreffer | null;
  aufAllesDa: (id: string) => void;
}

function Knoten({
  knoten,
  tiefe,
  oben,
  bestand,
  offen,
  umschalten,
  treffer,
  aufAllesDa,
}: KnotenEigenschaften) {
  const l = knoten.lagerort;
  if (treffer !== null && !treffer.sichtbar.has(l.id)) return null;
  const auf = treffer === null ? offen.has(l.id) : true;

  const v = vollstaendigkeitVon(bestand, l.id);
  const hatSoll = v.soll > 0 || v.fehltDarin > 0;
  const fehlt = v.fehlend.length + v.fehltDarin;
  const aufklappbar = knoten.kinder.length > 0 || knoten.anzahl > 0 || v.fehlend.length > 0;
  const name = l.standardziel || l.unbekannt ? l.bezeichnung : `${l.kennung} — ${l.bezeichnung}`;

  return (
    <li>
      <div className="flex gap-1" style={{ marginLeft: `${String(tiefe * EINZUG_PX)}px` }}>
        {aufklappbar ? (
          <button
            type="button"
            aria-expanded={auf}
            aria-label={`${kurz(l)} ${auf ? "zuklappen" : "aufklappen"}`}
            disabled={treffer !== null}
            onClick={() => {
              umschalten(l.id);
            }}
            className="min-h-12 min-w-12 shrink-0 rounded-xl bg-neutral-200 text-xl font-bold active:bg-neutral-300 dark:bg-neutral-700"
          >
            {auf ? "▾" : "▸"}
          </button>
        ) : (
          <span className="min-w-12 shrink-0" aria-hidden />
        )}
        <Link
          to={`/lagerort/${l.id}`}
          className="flex min-h-12 min-w-0 flex-1 items-center justify-between gap-2 rounded-xl bg-white p-3 active:bg-neutral-100 dark:bg-neutral-800"
        >
          <span className="flex min-w-0 flex-col gap-1">
            <span>
              <span className="font-semibold">{name}</span>
              {l.mobil && <span className="ml-2 text-xs text-neutral-500">mobil</span>}
              {!l.aktiv && <span className="ml-2 text-xs text-neutral-500">stillgelegt</span>}
            </span>
            {l.artikel_id !== null && oben !== null && (
              <span>
                <GehoertNach bestand={bestand} artikel_id={l.artikel_id} hier={oben} />
              </span>
            )}
          </span>
          <span className="flex shrink-0 items-center gap-2 text-sm">
            {hatSoll && <FehltSchild fehlt={fehlt} />}
            <span className="text-neutral-500" aria-label={`${String(knoten.anzahl)} Artikel`}>
              {knoten.anzahl}
            </span>
          </span>
        </Link>
      </div>
      {auf && (
        <KnotenInhalt
          knoten={knoten}
          tiefe={tiefe}
          oben={oben}
          bestand={bestand}
          offen={offen}
          umschalten={umschalten}
          treffer={treffer}
          aufAllesDa={aufAllesDa}
          fehlend={v.fehlend}
        />
      )}
    </li>
  );
}

/** Was in einem aufgeklappten Knoten steht: Plätze und Kisten, Artikel, was fehlt, „Alles da“. */
function KnotenInhalt({
  knoten,
  tiefe,
  bestand,
  offen,
  umschalten,
  treffer,
  aufAllesDa,
  fehlend,
}: KnotenEigenschaften & { fehlend: readonly string[] }) {
  const l = knoten.lagerort;
  const ansicht = behaelterAnsicht(bestand, l.id);
  // Bei einer Suche nur, worauf sie passt -- außer der Knoten selbst ist gemeint.
  const nur = treffer === null || treffer.orte.has(l.id) ? null : treffer.artikel;
  const alles = nur === null;
  const gruppen = (ansicht?.gruppen ?? [])
    .map((g) =>
      nur === null ? g : { ...g, eintraege: g.eintraege.filter((e) => nur.has(e.artikel.id)) },
    )
    .filter((g) => g.eintraege.length > 0);
  const zuBuchen = allesDaFuer(bestand, l.id).zeilen.length;
  const einzug = { marginLeft: `${String((tiefe + 1) * EINZUG_PX + 52)}px` };

  return (
    <div className="mt-2 flex flex-col gap-2">
      {knoten.kinder.length > 0 && (
        <ul className="flex flex-col gap-2">
          {knoten.kinder.map((k) => (
            <Knoten
              key={k.lagerort.id}
              knoten={k}
              tiefe={tiefe + 1}
              oben={l.id}
              bestand={bestand}
              offen={offen}
              umschalten={umschalten}
              treffer={treffer}
              aufAllesDa={aufAllesDa}
            />
          ))}
        </ul>
      )}
      {gruppen.length > 0 && (
        <ul className="flex flex-col gap-2" style={einzug} aria-label={`Inhalt von ${kurz(l)}`}>
          {gruppen.map((g) => (
            <li key={g.bezeichnung}>
              <GruppenZeile gruppe={g} bestand={bestand} hier={l.id} />
            </li>
          ))}
        </ul>
      )}
      {alles && fehlend.length > 0 && (
        <section style={einzug} aria-label={`Fehlt in ${kurz(l)}`} className="flex flex-col gap-2">
          <h3 className="text-sm font-semibold text-amber-800 dark:text-amber-300">Fehlt</h3>
          <FehltListe bestand={bestand} fehlend={fehlend} />
        </section>
      )}
      {zuBuchen > 0 && (
        <div style={einzug} className="flex flex-col">
          <Knopf
            art="haupt"
            onClick={() => {
              aufAllesDa(l.id);
            }}
          >
            {`Alles da – ${kurz(l)}`}
          </Knopf>
        </div>
      )}
    </div>
  );
}

export function Baum() {
  // Aus der lokalen Kopie -- was noch in der Warteschlange liegt, steht schon da.
  const bestand = useBestand();
  const online = useOnline();
  const navigate = useNavigate();
  // Suchbegriff und aufgeklappte Knoten in der Adresse: Nach dem Zurueck ist
  // der Baum wieder so, wie man ihn verlassen hat (Rueckmeldungen 1.1).
  const [eingabe, setEingabe] = useAdressText("suche");
  const [offen, setOffen] = useAdressMenge("offen");
  const [allesDa, setAllesDa] = useState<string | null>(null);
  const [neu, setNeu] = useState(false);
  const { setMeldung, anzeige } = useBuchungsmeldung();

  const baum = useMemo(() => (bestand === undefined ? undefined : lagerBaum(bestand)), [bestand]);
  const treffer = useMemo(
    () =>
      bestand === undefined || baum === undefined ? null : lagerSuchen(bestand, baum, eingabe),
    [bestand, baum, eingabe],
  );
  const umschalten = (id: string): void => {
    const danach = new Set(offen);
    if (danach.has(id)) danach.delete(id);
    else danach.add(id);
    setOffen(danach);
  };

  if (bestand === undefined || baum === undefined) return <Ladeanzeige was="Das Lager" />;

  if (allesDa !== null) {
    return (
      <AllesDa
        lagerort_id={allesDa}
        aufAbbruch={() => {
          setAllesDa(null);
        }}
        aufGebucht={(gebucht, ausgelassen) => {
          setAllesDa(null);
          rueckmeldungGeben(true);
          setMeldung({
            art: ausgelassen.length > 0 ? "warnung" : "gut",
            kopf: `✓ ${String(gebucht.length)} ${gebucht.length === 1 ? "Bewegung" : "Bewegungen"} gebucht`,
            zusatz:
              ausgelassen.length > 0
                ? `${String(ausgelassen.length)} ausgelassen: Ein Packstück hätte in sich selbst gesteckt.`
                : "Alles an seinem Stammplatz. Der Status bleibt, wie er war.",
            gebucht,
          });
        }}
      />
    );
  }
  if (neu) {
    return (
      <NeuesPackstueck
        ort_id={null}
        weiter="Zum Packstück"
        aufAbbruch={() => {
          setNeu(false);
        }}
        aufFertig={(l) => {
          void navigate(`/lagerort/${l.id}`);
        }}
      />
    );
  }

  const admin = sitzungLesen()?.benutzer.rolle === "administrator";
  if (baum.length === 0) {
    return (
      <Leer>
        Noch keine Lagerorte angelegt.{" "}
        {admin && (
          <Link to="/verwaltung/lagerorte" className="font-semibold underline">
            Jetzt anlegen
          </Link>
        )}
      </Leer>
    );
  }

  return (
    <div className="flex flex-col gap-4">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <h1 className="text-xl font-bold">Lager</h1>
        <span className="flex gap-4">
          <Link
            to="/etiketten"
            className="flex min-h-12 items-center text-sm text-blue-700 underline dark:text-blue-300"
          >
            Etiketten drucken
          </Link>
        </span>
      </div>

      {/*
        Zwei Knoepfe fuer Admins (ADR 0028): Orte pflegen und ein Packstueck
        anlegen. Jeder andere legt Packstuecke in „Wohin?“ an.
      */}
      {admin && (
        <div className="grid grid-cols-2 gap-2">
          <Link
            to="/verwaltung/lagerorte"
            className="flex min-h-12 items-center justify-center rounded-xl bg-neutral-200 px-4 font-semibold active:bg-neutral-300 dark:bg-neutral-700"
          >
            Orte pflegen
          </Link>
          <Knopf
            disabled={!online}
            onClick={() => {
              setNeu(true);
            }}
          >
            + Packstück
          </Knopf>
        </div>
      )}
      {admin && !online && (
        <p className="-mt-2 text-sm text-neutral-600 dark:text-neutral-300">
          Ein Packstück anlegen geht nur mit Netz.
        </p>
      )}

      {anzeige}

      <input
        className="min-h-12 rounded-xl border-2 border-neutral-300 bg-white px-4 text-base dark:border-neutral-600 dark:bg-neutral-800 dark:text-white"
        value={eingabe}
        onChange={(e) => {
          setEingabe(e.target.value);
        }}
        placeholder="Im Lager suchen"
        aria-label="Im Lager suchen"
        autoComplete="off"
        enterKeyHint="search"
      />
      {treffer !== null && treffer.sichtbar.size === 0 && (
        <p className="text-sm text-neutral-600 dark:text-neutral-300">Nichts gefunden.</p>
      )}

      <ul className="flex flex-col gap-2" aria-label="Lager">
        {baum.map((k) => (
          <Knoten
            key={k.lagerort.id}
            knoten={k}
            tiefe={0}
            oben={null}
            bestand={bestand}
            offen={offen}
            umschalten={umschalten}
            treffer={treffer}
            aufAllesDa={setAllesDa}
          />
        ))}
      </ul>
    </div>
  );
}
